/**
 * 슬롯 ↔ 카카오 애드핏 광고단위 매핑.
 *
 * 프로모션 슬롯에 등록된(하우스) 배너가 없을 때, 해당 슬롯에 매핑된 애드핏 광고를
 * fallback 으로 노출한다(업계 표준 waterfall: 하우스 우선 → 네트워크 fallback).
 *
 * 운영 방법:
 * - 슬롯을 애드핏으로 채우려면, 애드핏에서 그 슬롯 크기에 맞는 광고단위를 발급해
 *   아래 매핑에 { adUnit, width, height } 를 넣는다. 매핑이 없는 슬롯은 애드핏 미노출
 *   (기존 fallback prop 이 있으면 그것, 없으면 빈 자리).
 * - 광고단위 ID(DAN-...)는 HTML 에 노출되는 공개값이라 코드 상수로 둬도 무방하다.
 * - 애드핏 규격만 지원(728x90 / 300x250 / 250x250 / 160x600 / 320x100 / 320x50 등).
 *   슬롯 지면 크기와 애드핏 규격이 다르면 근접 규격으로 발급하고 지면을 맞춘다.
 *
 * 영구지침: docs/plan/2026-08-05-promotion-adfit-integration.md
 */

export interface AdfitUnit {
  /** 애드핏 광고단위 ID (DAN-xxxx) */
  adUnit: string
  width: number
  height: number
  /**
   * 모바일(lg 미만) 전용 크기. 한 곳에서만 렌더되는 반응형 슬롯(guide_bottom 등)에서
   * PC용 큰 배너(728x90)가 모바일 폭을 넘겨 가로 스크롤을 만드는 것을 막는다.
   * ※ 한 페이지에서 2번 렌더되는 슬롯(main_hero/main_card_slider 등)은 이 필드가 아니라
   *   위치별 별도 slotKey(_mobile)로 분리해야 애드핏 중복 노출을 피한다.
   */
  mobile?: { adUnit: string; width: number; height: number }
}

/**
 * 발급 완료된 슬롯만 등록. 나머지 슬롯은 각 크기로 애드핏 광고단위 발급 후 추가한다.
 *
 * 발급 필요 목록(슬롯 → 권장 애드핏 규격):
 *   main_hero(1200x300)        → 728x90
 *   main_card_slider(940x140)  → 728x90
 *   main_my_below(360x140)     → 300x250 또는 320x100
 *   main_side_ad_grid(600x600) → 250x250
 *   main_mobile_inline         → 320x100
 *   main_shop_card             → 250x250
 *   search_list_4 / search_list_12(400x140) → 320x100
 *   simulator_result_side(300x250) → 300x250
 *   guide_bottom(600x200)      → 728x90
 */
export const SLOT_ADFIT_UNITS: Record<string, AdfitUnit> = {
  // === 승인 완료된 광고단위 5종 (2026-08-05) ===
  // ⚠️ 같은 광고단위를 한 페이지에 2번 넣으면 애드핏이 한쪽만 노출한다.
  //    그래서 '한 페이지 안에서 같은 크기 슬롯'은 1개만 매핑하고, 나머지는 아래 '중복 제외'에 둔다.
  //    그 자리도 애드핏을 켜려면 같은 크기 광고단위를 하나 더 발급해 여기에 추가하면 된다.

  // 시설상세 PC 우측 사이드 — 160x600 세로배너
  facility_detail_side:  { adUnit: 'DAN-e4PKM1iNLpvXxMyG', width: 250, height: 250 },
  // 시설상세 모바일 본문 — 320x100(세로배너가 모바일을 과점하지 않도록 별도 슬롯/크기).
  // FacilityDetailPage 의 lg:hidden 본문 위치에서만 사용 → 페이지당 각 광고단위 1회 삽입.
  facility_detail_side_mobile: { adUnit: 'DAN-l8paXNb9UTkwuiqb', width: 320, height: 100 },
  // 시뮬레이터 결과 사이드 (단독 페이지)
  simulator_result_side: { adUnit: 'DAN-XpeaiY6PK1SLdw6c', width: 300, height: 250 },
  // 홈 상단 가로 배너 (홈의 728x90 대표 슬롯)
  main_hero:             { adUnit: 'DAN-hlgyfq38Lh4vK0P8', width: 728, height: 90 },
  // 가이드/블로그 하단 (1곳 반응형 렌더) — PC 728x90, 모바일 320x100(가로 스크롤 방지)
  guide_bottom:          {
    adUnit: 'DAN-kic8ie59HUKcQFGI', width: 728, height: 90,
    mobile: { adUnit: 'DAN-2BOxmIZEKwYIXs6J', width: 320, height: 100 },
  },
  // 홈 우측 정사각 그리드 자리 (홈의 250x250 대표 슬롯)
  main_side_ad_grid:     { adUnit: 'DAN-cR2ejciP2x0j4ssV', width: 250, height: 250 },
  // 홈 우측 로그인카드 아래 (데스크톱) — 320x100
  main_my_below:         { adUnit: 'DAN-xE2bahgu1wFAs3Yb', width: 320, height: 100 },
  // 홈 모바일 인라인 (모바일 홈에서 main_my_below 와 같은 화면이라 별도 광고단위)
  main_mobile_inline:    { adUnit: 'DAN-lteI1JY7j76sd6P1', width: 320, height: 100 },
  // 검색결과 4번째 자리 (검색페이지의 320x100 대표 슬롯)
  search_list_4:         { adUnit: 'DAN-zl5EN94ppuYWtlqC', width: 320, height: 100 },

  // === 2차 발급 광고단위로 중복 자리까지 활성 (2026-08-05) ===
  // 같은 페이지·같은 크기라 별도 광고단위(2번째)를 발급해 연결.
  main_card_slider:      { adUnit: 'DAN-aA4gar3x8EZF63Zy', width: 728, height: 90 },  // 홈 728x90 2번째
  main_shop_card:        { adUnit: 'DAN-aJ4OnEnUbYmyl2vB', width: 250, height: 250 }, // 홈 250x250 2번째
  search_list_12:        { adUnit: 'DAN-DAptvRf1WHhGLiOI', width: 320, height: 100 }, // 검색 320x100 2번째

  // === 홈 모바일 상단 전용 320x100 (PC는 728, 모바일은 별도 slotKey로 분리) ===
  main_hero_mobile:         { adUnit: 'DAN-OSx7uSa5yyCN38BJ', width: 320, height: 100 },
  main_card_slider_mobile:  { adUnit: 'DAN-VqHmVBjtDsoEvH2v', width: 320, height: 100 },

  // === 게시판 상세 좌우 세로배너 160x600 (PC 초광폭에서만 노출) ===
  community_post_side_left:  { adUnit: 'DAN-cm223QcDedcvG9iE', width: 160, height: 600 },
  community_post_side_right: { adUnit: 'DAN-3SffwHLX2eeOS6JR', width: 160, height: 600 },

  // === 게시판 목록 좌우 세로배너 160x600 (PC 초광폭에서만 노출) ===
  community_board_side_left:  { adUnit: 'DAN-M1kIzL0ZLE4szyA0', width: 160, height: 600 },
  community_board_side_right: { adUnit: 'DAN-8OM9qwSLc1VaJg3n', width: 160, height: 600 },

  // === 요양비용 계산기 좌우 세로배너 160x600 (콘텐츠 max-w-6xl 이라 2xl 초광폭에서만 노출) ===
  simulator_side_left:  { adUnit: 'DAN-dIeccZD5ANujpNUp', width: 160, height: 600 },
  simulator_side_right: { adUnit: 'DAN-xSOGZHhj6Kb8DpzM', width: 160, height: 600 },

  // === 시설 상세 좌우 세로배너 160x600 (콘텐츠 max-w-6xl 이라 2xl 초광폭에서만 노출) ===
  facility_detail_sticky_left:  { adUnit: 'DAN-JSsZFRWbFsxTY0I6', width: 160, height: 600 },
  facility_detail_sticky_right: { adUnit: 'DAN-kMuOavjFekVjCjzi', width: 160, height: 600 },

  // === 시설검색 우측 세로배너 160x600 (지도 분할형이라 xl 이상에서 지도 옆 전용 컬럼) ===
  facility_search_side_right: { adUnit: 'DAN-jTHGcRq5zymnfYx8', width: 160, height: 600 },

  // === 메인 홈 좌우 세로배너 160x600 (콘텐츠 max-w-6xl 이라 2xl 초광폭에서만 노출) ===
  home_side_left:  { adUnit: 'DAN-wXW2F6jPXMVX45O4', width: 160, height: 600 },
  home_side_right: { adUnit: 'DAN-PoWKooONAMeEjOWR', width: 160, height: 600 },

  // === 체크리스트 좌우 세로배너 160x600 (콘텐츠 max-w-2xl 이라 xl 이상 노출) ===
  checklist_side_left:  { adUnit: 'DAN-8JCDBAQMP21JZoFN', width: 160, height: 600 },
  checklist_side_right: { adUnit: 'DAN-Dt5Q9NLnTkOC1VgF', width: 160, height: 600 },

  // 통합돌봄계산기 좌우 세로배너 (2026-08-25 발급)
  carecalc_side_left:  { adUnit: 'DAN-XS3LTG5AwKPx5vQv', width: 160, height: 600 },
  carecalc_side_right: { adUnit: 'DAN-DWsrlqUqDroPy7B9', width: 160, height: 600 },
}

/** 슬롯키에 매핑된 애드핏 광고단위 반환(없으면 undefined). */
export function getAdfitUnit(slotKey: string): AdfitUnit | undefined {
  return SLOT_ADFIT_UNITS[slotKey]
}
